import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../../../../i18n';
import Icon from '../../../../components/AppIcon';

const LanguageSelector = ({ selectedLanguage, onLanguageSelect, selectedCountry }) => {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    // Top 10 most common languages for initial implementation
    const topLanguageCodes = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'ja', 'zh', 'ar'];

    // Filter to top 10 languages for now
    const topLanguages = useMemo(() =>
        supportedLanguages.filter(lang => topLanguageCodes.includes(lang.code)),
        []
    );

    // Get regional language suggestions based on selected country
    const getRegionalLanguages = (countryCode) => {
        const countryLanguageMap = {
            'US': ['en'],
            'CA': ['en', 'fr'],
            'GB': ['en'],
            'AU': ['en'],
            'DE': ['de'],
            'FR': ['fr'],
            'ES': ['es'],
            'IT': ['it'],
            'JP': ['ja'],
            'IN': ['en'],
            'BR': ['pt'],
            'MX': ['es'],
            'CN': ['zh'],
            'KR': ['ko'],
            'RU': ['ru'],
            'SA': ['ar'],
            'TR': ['tr'],
            'PL': ['pl'],
            'NL': ['nl'],
            'SE': ['sv'],
            'DK': ['da'],
            'NO': ['no'],
            'FI': ['fi'],
            'IL': ['he'],
            'TH': ['th'],
            'VN': ['vi'],
            'UA': ['uk'],
            'CZ': ['cs'],
            'SK': ['sk'],
            'HU': ['hu'],
            'RO': ['ro'],
            'BG': ['bg'],
            'HR': ['hr'],
            'EE': ['et'],
            'LV': ['lv'],
            'LT': ['lt'],
            'SI': ['sl'],
        };

        return countryLanguageMap[countryCode] || ['en'];
    };

    const regionalLanguages = useMemo(() => {
        if (!selectedCountry) return [];

        const languageCodes = getRegionalLanguages(selectedCountry.code);
        return topLanguages.filter(lang => languageCodes.includes(lang.code));
    }, [selectedCountry, topLanguages]);

    const handleLanguageSelect = (language) => {
        onLanguageSelect(language);
        i18n.changeLanguage(language.code);
        setIsOpen(false);
    };

    const getLanguageDisplay = (language) => {
        // Show native script alongside English for non-Latin scripts
        const nativeScriptLanguages = ['ar', 'he', 'hi', 'ja', 'ko', 'zh', 'ru', 'th', 'vi', 'uk', 'bg'];

        if (nativeScriptLanguages.includes(language.code)) {
            return (
                <div className="flex flex-col">
                    <span className="font-medium">{language.name}</span>
                    <span className="text-xs text-muted-foreground">{language.name}</span>
                </div>
            );
        }

        return <span className="font-medium">{language.name}</span>;
    };

    return (
        <div className="relative">
            {/* Selected Language Display / Dropdown Trigger */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between p-3 border border-input rounded-lg cursor-pointer hover:border-primary-300 transition-colors bg-white"
            >
                {selectedLanguage ? (
                    <div className="flex items-center space-x-3">
                        <span className="text-xl">{selectedLanguage.flag}</span>
                        <div className="flex items-center space-x-2">
                            {getLanguageDisplay(selectedLanguage)}
                        </div>
                    </div>
                ) : (
                    <span className="text-muted-foreground">Select your language</span>
                )}
                <Icon
                    name="ChevronDown"
                    size={20}
                    className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-input rounded-lg shadow-lg z-50 max-h-80 overflow-hidden">
                    <div className="overflow-y-auto max-h-80">
                        {/* Regional Languages */}
                        {regionalLanguages.length > 0 && (
                            <div className="p-2">
                                <div className="text-xs font-medium text-muted-foreground px-2 py-1 mb-1">
                                    Recommended for {selectedCountry?.name}
                                </div>
                                {regionalLanguages.map((language) => (
                                    <button
                                        key={language.code}
                                        onClick={() => handleLanguageSelect(language)}
                                        className="w-full flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-surface transition-colors text-left"
                                    >
                                        <span className="text-lg">{language.flag}</span>
                                        {getLanguageDisplay(language)}
                                        {selectedLanguage?.code === language.code && (
                                            <Icon name="Check" size={16} className="ml-auto text-primary" />
                                        )}
                                    </button>
                                ))}
                                <div className="border-t border-input my-2"></div>
                            </div>
                        )}

                        {/* Popular Languages */}
                        <div className="p-2">
                            <div className="text-xs font-medium text-muted-foreground px-2 py-1 mb-1">
                                Popular Languages
                            </div>
                            {topLanguages.map((language) => (
                                <button
                                    key={language.code}
                                    onClick={() => handleLanguageSelect(language)}
                                    className="w-full flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-surface transition-colors text-left"
                                >
                                    <span className="text-lg">{language.flag}</span>
                                    {getLanguageDisplay(language)}
                                    {selectedLanguage?.code === language.code && (
                                        <Icon name="Check" size={16} className="ml-auto text-primary" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

export default LanguageSelector; 